import classnames from 'classnames';
import React, {useRef} from 'react';

import {Text} from '@albert-io/atomic';

import {isSupplementAnchor} from './annotationAnchors';
import {useAnnotationModeStore} from './AnnotationModeStore';
import AnnotationNoteEditButton from './AnnotationNoteEditButton';
import {HIGHLIGHT_BACKGROUND_CLASS} from './annotationHighlightStyles';
import type {QuestionNote} from './annotationNotes';
import {supplementNoteLabel} from './annotationSelectors';

interface AnnotationNotesPanelItemProps {
  note: QuestionNote;
  isEditing: boolean;
  /** Hands up the row the editor hangs from. Absent where the note may not be edited. */
  onEdit?: (note: QuestionNote, row: HTMLLIElement) => void;
}

/**
 * One note, quoted under the text it was written about. An underlined mark is
 * quoted underlined rather than filled, matching AnnotationNoteList.
 */
export default function AnnotationNotesPanelItem({
  note,
  isEditing,
  onEdit
}: AnnotationNotesPanelItemProps) {
  const itemRef = useRef<HTMLLIElement>(null);
  const {anchor, color, type} = note.annotation;
  const mountedSupplements = useAnnotationModeStore((state) => state.mountedSupplements);

  return (
    <li
      ref={itemRef}
      className='rounded-x1-half gap-x1 p-x1-half background-primary border-border-primary flex flex-col border border-solid'
      data-testid='annotation-notes-panel-item'
    >
      <div className='flex items-center'>
        <Text
          size='s'
          color='primary'
          className={classnames('rounded-x1 p-x0-half min-w-x0 flex-1 truncate', {
            [HIGHLIGHT_BACKGROUND_CLASS[color]]: type === 'highlight',
            underline: type === 'underline'
          })}
        >
          {isSupplementAnchor(anchor)
            ? supplementNoteLabel(mountedSupplements, anchor.regionId)
            : `“${anchor.exact}”`}
        </Text>
        {onEdit && (
          <AnnotationNoteEditButton
            anchor={anchor}
            isEditing={isEditing}
            className='ml-x1'
            onClick={() => {
              if (!itemRef.current) {
                return;
              }
              onEdit(note, itemRef.current);
            }}
          />
        )}
      </div>
      {/* Notes merged from combined marks are newline-separated (annotationMerge). */}
      <Text size='s' color='primary' className='wrap-break-word whitespace-pre-wrap'>
        {note.annotation.note}
      </Text>
    </li>
  );
}
